import { CommonModule } from '@angular/common'
import { Component, inject, Input, OnInit } from '@angular/core'
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs'
import { map } from 'rxjs/operators'

import { Enums } from '@cyborg-monorepo/hunter/common'
import { TagComponent, TypographyComponent } from '@hyperkit'

import {
  displayName,
  libraryDisplayName,
  tagDisplayName,
} from '../../../../helpers/display-names'
import {
  DEFAULT_SEARCH_STATE,
  SearchStateExtended,
  SearchStateService,
} from '../../../research/services/search-state.service'

export enum FacetButtonType {
  term = 'term',
  tag = 'tag',
  library = 'library',
}

export interface FacetButtonItem {
  type: FacetButtonType
  category?: string | Enums.CyborgIndexEnum | Enums.CyborgTagTypeEnum
  value: string
}

interface FacetButtonItemNormalized {
  data: FacetButtonItem
  category: string
  value: string
  active: boolean
}

@Component({
  standalone: true,
  selector: 'cyborg-facet-buttons',
  imports: [CommonModule, TagComponent, TypographyComponent],
  templateUrl: './facet-buttons.component.html',
  styleUrls: ['./facet-buttons.component.scss'],
})
export class FacetButtonsComponent implements OnInit {
  searchStateService = inject(SearchStateService)

  private buttonItems$: Subject<FacetButtonItem[]> = new BehaviorSubject([])
  public buttons$: Observable<FacetButtonItemNormalized[]>
  public groupedButtons: Map<string, Array<FacetButtonItemNormalized>>
  public library: Enums.CyborgIndexEnum[]
  public touched: boolean

  @Input('facets')
  public set facetValues(facets: FacetButtonItem[]) {
    this.buttonItems$.next(facets)
  }
  @Input() public groupByCategory: boolean

  @Input() public closable = true

  public ngOnInit(): void {
    this.searchStateService.state.subscribe((state: SearchStateExtended) => {
      this.library = state.library
      this.touched = state.touched
    })

    if (this.groupByCategory) {
      this.getNormalizedButtons().subscribe((buttons) => {
        const array = buttons as FacetButtonItemNormalized[]
        this.groupedButtons = this.groupBy(array, (button) => button.category)
      })
    } else {
      this.buttons$ = this.getNormalizedButtons()
    }
  }

  public onButtonClick(filter: FacetButtonItemNormalized): void {
    if (filter.active) {
      this.removeFilter(filter)
    } else {
      this.addFilter(filter)
    }
  }

  private removeFilter(filter: FacetButtonItemNormalized): void {
    switch (filter.data.type) {
      case FacetButtonType.tag:
        this.searchStateService.deleteFilter(
          filter.data.category,
          filter.data.value,
        )
        break
      case FacetButtonType.term:
        this.searchStateService.removeTerm(filter.data.value)
        break
      case FacetButtonType.library:
        if (this.library.length > 1) {
          this.searchStateService.removeLibrary(
            filter.data.value as Enums.CyborgIndexEnum,
            true,
          )
        } else {
          this.searchStateService.setLibrary(
            DEFAULT_SEARCH_STATE.library,
            false,
          )
        }
    }
  }

  private addFilter(filter: FacetButtonItemNormalized): void {
    switch (filter.data.type) {
      case FacetButtonType.tag:
        this.searchStateService.addFilter(
          filter.data.category,
          filter.data.value,
        )
        break
      case FacetButtonType.term:
        this.searchStateService.setTerm(filter.data.value)
        break
      case FacetButtonType.library:
        this.searchStateService.setLibrary(
          [filter.data.value as Enums.CyborgIndexEnum],
          true,
        )
    }
  }

  public groupBy(
    list: FacetButtonItemNormalized[],
    keyGetter,
  ): Map<string, Array<FacetButtonItemNormalized>> {
    const map = new Map<string, Array<FacetButtonItemNormalized>>()
    list.forEach((item) => {
      const key = keyGetter(item)
      const collection = map.get(key)
      if (!collection) {
        map.set(key, [item])
      } else {
        collection.push(item)
      }
    })
    return map
  }

  private getNormalizedButtons(): Observable<FacetButtonItemNormalized[]> {
    const normalizedButtons = combineLatest([
      this.searchStateService.state,
      this.buttonItems$,
    ]).pipe(
      map(([searchState, buttons]) => {
        const normalizedButtons: FacetButtonItemNormalized[] = []
        if (buttons)
          buttons.forEach((item) => {
            let category = item.category
            let value: string = item.value
            let active = false
            switch (item.type) {
              case FacetButtonType.library:
                category = 'Library'
                value = libraryDisplayName(item.value as Enums.CyborgIndexEnum)
                active = searchState.library.includes(
                  item.value as Enums.CyborgIndexEnum,
                )
                break
              case FacetButtonType.term:
                category = 'Term'
                active = searchState.term.includes(item.value)
                break
              case FacetButtonType.tag:
                category = tagDisplayName(
                  item.category as Enums.CyborgTagTypeEnum,
                )
                value = displayName(item.value)
                active = searchState.filtersDenormalized.some((filter) => {
                  return (
                    filter.type === item.category &&
                    filter.filter === item.value
                  )
                })
                break
            }
            normalizedButtons.push({
              data: item,
              category,
              value,
              active,
            })
          })

        return normalizedButtons
      }),
    )
    return normalizedButtons
  }
}
